import React from 'react';
import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  SvgIcon,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Search as SearchIcon } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { CustomInputEvent, CustomKeyboardEvent } from 'src/types';
import { CategoryListDropdown } from 'src/components/filters/CategoryDropdown';
import { Autocomplete } from '@material-ui/lab';
import { FilterStockEnum } from 'src/enums/product/status';
import { snakeToNaturalCase, toTitleCase } from 'src/utils';
import useResolution from 'src/hooks/useResolution';
import { usePermissions } from 'src/hooks';

interface Props {
  onFilterPress?: () => void;
  className?: string;
  onSearchProduct: (text?: string, category_id?: number) => void;
  onCategoryChange: (category_id?: number) => void;
  searchInput: string;
  productFilter: FilterStockEnum;
  setProductFilter: (filter: FilterStockEnum) => void;
  categoryId?: number;
  onSearchInputChange: (e: CustomInputEvent) => void;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  cardContent: {
    flexDirection: 'row'
  }
}));

const component = ({
  className,
  searchInput,
  categoryId,
  productFilter,
  setProductFilter,
  onSearchProduct,
  onSearchInputChange,
  onCategoryChange,
  ...rest
}: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { sm } = useResolution();
  const { canAddProducts } = usePermissions();

  const handleChange = (event: CustomInputEvent) => {
    onSearchInputChange(event);
  };

  const onSearchClick = () => {
    if (categoryId !== 0) {
      onSearchProduct(searchInput, categoryId);
    } else {
      onSearchProduct(searchInput);
    }
  };

  const onKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      if (categoryId !== 0) {
        onSearchProduct(searchInput, categoryId);
      } else {
        onSearchProduct(searchInput);
      }
    }
  };

  const onHandleChangeCategory = (category_id?: number) => {
    onCategoryChange(category_id);
  };

  const getOptionLabel = (val: string) => {
    return toTitleCase(snakeToNaturalCase(val));
  };

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography color="textPrimary" gutterBottom variant="h3">
          Products
        </Typography>
        {canAddProducts && (
          <Button
            onClick={() => navigate('/app/products/add')}
            color="primary"
            variant="contained"
          >
            add product
          </Button>
        )}
      </Box>
      <Box mt={3}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item sm={4} md={3} xs={12}>
                <Box
                  maxWidth={500}
                  sx={{ display: 'flex', flexDirection: 'row' }}
                >
                  <TextField
                    style={{
                      borderTopLeftRadius: 10,
                      borderBottomLeftRadius: 10
                    }}
                    fullWidth
                    value={searchInput}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon fontSize="small" color="action">
                            <SearchIcon />
                          </SvgIcon>
                        </InputAdornment>
                      )
                    }}
                    placeholder={'Search via product/SKU'}
                    variant="outlined"
                    onKeyPress={onKeyPress}
                  />
                </Box>
              </Grid>

              <Grid item sm={4} md={3} xs={12}>
                <Box maxWidth={500}>
                  <CategoryListDropdown
                    onHandleCategories={(categoryId: number) =>
                      onHandleChangeCategory(categoryId)
                    }
                  />
                </Box>
              </Grid>
              <Grid item sm={4} md={3} xs={12}>
                <Autocomplete
                  options={Object.values(FilterStockEnum)}
                  value={productFilter}
                  fullWidth
                  getOptionLabel={getOptionLabel}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Product Filters"
                      variant="outlined"
                    />
                  )}
                  onChange={(_, val) =>
                    setProductFilter(val ?? FilterStockEnum.All)
                  }
                />
              </Grid>

              <Grid
                alignItems="center"
                item
                xs={12}
                md={1}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Button
                  onClick={onSearchClick}
                  color="primary"
                  variant="contained"
                  style={{ width: sm ? '8em' : 'initial' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export const Toolbar = React.memo(component);
